import { birthdayPromptAtom, birthdayStatusAtom, closeBirthdayPromptAtom } from "@/atoms/birthday-prompt"
import { BirthdaySelects, type BirthdaySelection } from "@/components/birthday/BirthdaySelects"
import { showToast } from "@/components/feedback"
import { BottomSheet } from "@/components/modals/BottomSheet"
import { Button, CloseButton, FieldError, IconBadge, SheetBody, SheetHeader, Text } from "@/components/ui"
import { useModalStack } from "@/contexts/ModalContext"
import { declineBirthdayPrompt, saveBirthday } from "@/domain/birthday/api"
import { parseBirthdayDate } from "@/domain/birthday/calendar"
import { Cake } from "@/icons"
import * as Dialog from "@radix-ui/react-dialog"
import { useAtomValue, useSetAtom } from "jotai"
import { useState, type FormEvent } from "react"

/**
 * Birthday question in the login sheet's language: Salvar stores day and month, Pular (or the X, scrim, swipe) asks
 * again at the next order attempt, "Não quero informar" is stored on the profile so no device asks again.
 * Waits for any other open sheet to close first, so it never stacks on top of one.
 */
export function BirthdayPromptSheet() {
  const { open, ticket } = useAtomValue(birthdayPromptAtom)
  const close = useSetAtom(closeBirthdayPromptAtom)
  const { hasOpenModal } = useModalStack()
  const [shown, setShown] = useState(false)
  const visible = open && (shown || !hasOpenModal)
  if (visible !== shown) setShown(visible)

  return (
    <BottomSheet
      open={visible}
      describedByChildren
      keepOnFocusOutside
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <Dialog.Close asChild>
        <CloseButton className="absolute right-4 top-4" />
      </Dialog.Close>
      <BirthdayPromptForm key={ticket} onDone={close} />
    </BottomSheet>
  )
}

type PendingAction = "save" | "decline" | null

type BirthdayPromptFormProps = {
  onDone: () => void
}

function BirthdayPromptForm({ onDone }: BirthdayPromptFormProps) {
  const refreshStatus = useSetAtom(birthdayStatusAtom)
  const [selection, setSelection] = useState<BirthdaySelection>({ day: "", month: "" })
  const [pending, setPending] = useState<PendingAction>(null)
  const [failed, setFailed] = useState(false)
  const date = parseBirthdayDate(selection.day, selection.month)
  const busy = pending !== null

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!date || busy) return
    setPending("save")
    setFailed(false)
    try {
      await saveBirthday(date)
      refreshStatus()
      showToast.success("Prontinho, anotamos seu aniversário!")
      onDone()
    } catch {
      setFailed(true)
      setPending(null)
    }
  }

  async function decline() {
    if (busy) return
    setPending("decline")
    try {
      await declineBirthdayPrompt()
      refreshStatus()
    } catch {
      showToast.error("Não deu pra registrar sua escolha agora. Você pode tocar em Pular.")
      setPending(null)
      return
    }
    onDone()
  }

  return (
    <SheetBody className="mx-auto max-h-sheet w-full max-w-md pb-safe-md pt-2">
      <div className="mb-5 flex justify-center">
        <IconBadge icon={Cake} size="xl" variant="solid" tone="primary" />
      </div>

      <SheetHeader
        align="center"
        title="Quando é seu aniversário?"
        description="Conte o dia e o mês. A loja pode preparar um mimo especial pra você nessa data."
        className="px-0 pb-6"
      />

      <form className="flex flex-col gap-5" onSubmit={(event) => void save(event)} noValidate>
        <BirthdaySelects
          day={selection.day}
          month={selection.month}
          disabled={busy}
          onChange={(next) => {
            setSelection(next)
            setFailed(false)
          }}
        />

        {failed && (
          <FieldError className="-mt-2 animate-fade-in motion-reduce:animate-none">
            Não deu pra salvar agora. Tente de novo ou toque em Pular.
          </FieldError>
        )}

        <div className="mt-1 flex flex-col gap-2">
          <Button type="submit" size="lg" fullWidth disabled={!date || busy} isLoading={pending === "save"}>
            {pending === "save" ? "Salvando..." : "Salvar"}
          </Button>
          <Button type="button" variant="ghost" tone="neutral" size="md" fullWidth disabled={busy} onClick={onDone}>
            Pular
          </Button>
          <Button
            type="button"
            variant="link"
            tone="neutral"
            size="auto"
            disabled={busy}
            isLoading={pending === "decline"}
            onClick={() => void decline()}
            className="mx-auto mt-1 underline-offset-auto"
          >
            <Text as="span" size="sm" tone="muted">
              Não quero informar
            </Text>
          </Button>
        </div>
      </form>
    </SheetBody>
  )
}
